import { Badge, Button } from '@iptv/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import { useState } from 'react';

import { adminFetch } from '../lib/api';
import { SortableTh, useTableSort } from '../lib/useTableSort';

interface OrderRow {
  id: number;
  order_ref: string;
  status: string;
  total_cents: number;
  currency: string;
  payment_provider: string | null;
  // Kooneo / Stripe / NOWPayments embed URL. Surfaced as an "Open
  // payment session" deep-link so support can see what the customer
  // is currently looking at without leaving the admin.
  payment_url: string | null;
  created_at: string;
  paid_at: string | null;
}

const STATUSES = ['', 'awaiting_payment', 'paid', 'cancelled', 'refunded', 'failed'];

function formatPrice(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(cents / 100);
}

function statusBadge(status: string) {
  if (status === 'paid') return <Badge variant="success">{status}</Badge>;
  if (status === 'awaiting_payment') return <Badge variant="warning">{status}</Badge>;
  if (status === 'cancelled' || status === 'failed')
    return <Badge variant="destructive">{status}</Badge>;
  return <Badge variant="secondary">{status}</Badge>;
}

export function OrdersPage() {
  const [status, setStatus] = useState('');
  const queryClient = useQueryClient();
  const orders = useQuery({
    queryKey: ['admin', 'orders', status],
    queryFn: () =>
      adminFetch<OrderRow[]>('/api/v1/admin/orders', {
        query: { status: status || undefined, limit: 200 },
      }),
  });
  const { sorted, sortKey, sortDir, toggleSort } = useTableSort<OrderRow>(orders.data, {
    initialKey: 'created_at',
    initialDir: 'desc',
  });

  const markPaid = useMutation({
    mutationFn: (id: number) =>
      adminFetch(`/api/v1/admin/orders/${id}/mark-paid`, {
        method: 'POST',
        body: { note: 'Marked manually from admin' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'orders'] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <label htmlFor="status-filter" className="text-sm text-muted-foreground">
          Filter:
        </label>
        <select
          id="status-filter"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="rounded-md border bg-background px-2 py-1 text-sm"
        >
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s || 'all'}
            </option>
          ))}
        </select>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-secondary/50">
            <tr>
              <SortableTh sortKey="order_ref" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Ref
              </SortableTh>
              <SortableTh sortKey="total_cents" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Total
              </SortableTh>
              <SortableTh
                sortKey="payment_provider"
                current={sortKey}
                dir={sortDir}
                onSort={toggleSort}
              >
                Provider
              </SortableTh>
              <SortableTh sortKey="status" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Status
              </SortableTh>
              <SortableTh sortKey="created_at" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Created
              </SortableTh>
              <SortableTh sortKey="paid_at" current={sortKey} dir={sortDir} onSort={toggleSort}>
                Paid
              </SortableTh>
              <th className="px-3 py-2 text-right text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.isPending && (
              <tr>
                <td colSpan={7} className="px-3 py-4 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {sorted.map((o) => (
              <tr key={o.id} className="border-t">
                <td className="px-3 py-2 font-mono text-xs">{o.order_ref}</td>
                <td className="px-3 py-2">{formatPrice(o.total_cents, o.currency)}</td>
                <td className="px-3 py-2 text-muted-foreground">{o.payment_provider ?? '—'}</td>
                <td className="px-3 py-2">{statusBadge(o.status)}</td>
                <td className="px-3 py-2 text-xs">{new Date(o.created_at).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">
                  {o.paid_at ? new Date(o.paid_at).toLocaleString() : '—'}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    {o.payment_url && (
                      <a
                        href={o.payment_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs hover:bg-secondary"
                        title="Open the customer's payment session in a new tab"
                      >
                        <ExternalLink className="h-3 w-3" />
                        {o.payment_provider === 'kooneo' ? 'Open Kooneo' : 'Open payment'}
                      </a>
                    )}
                    {o.status === 'awaiting_payment' && (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={markPaid.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Mark order ${o.order_ref} as paid? This triggers credentials provisioning and an email to the customer.`,
                            )
                          ) {
                            markPaid.mutate(o.id);
                          }
                        }}
                      >
                        Mark paid
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
